/**
 * Color Design Tokens
 * Core color palette for the application
 */

// Base colors with full shade range
export const blue = {
  50: '#e3f2fd',
  100: '#bbdefb',
  200: '#90caf9',
  300: '#64b5f6',
  400: '#42a5f5',
  500: '#2196f3', // Primary
  600: '#1e88e5',
  700: '#1976d2',
  800: '#1565c0',
  900: '#0d47a1',
};

export const grey = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
};

export const green = {
  50: '#e8f5e9',
  100: '#c8e6c9',
  200: '#a5d6a7',
  300: '#81c784',
  400: '#66bb6a',
  500: '#4caf50', // Success
  600: '#43a047',
  700: '#388e3c',
  800: '#2e7d32',
  900: '#1b5e20',
};

export const orange = {
  50: '#fff3e0',
  100: '#ffe0b2',
  200: '#ffcc80',
  300: '#ffb74d',
  400: '#ffa726',
  500: '#ff9800', // Warning
  600: '#fb8c00',
  700: '#f57c00',
  800: '#ef6c00',
  900: '#e65100',
};

export const red = {
  50: '#ffebee',
  100: '#ffcdd2',
  200: '#ef9a9a',
  300: '#e57373',
  400: '#ef5350',
  500: '#f44336', // Error
  600: '#e53935',
  700: '#d32f2f',
  800: '#c62828',
  900: '#b71c1c',
};

export const purple = {
  50: '#f3e5f5',
  100: '#e1bee7',
  200: '#ce93d8',
  300: '#ba68c8',
  400: '#ab47bc',
  500: '#9c27b0',
  600: '#8e24aa',
  700: '#7b1fa2',
  800: '#6a1b9a',
  900: '#4a148c',
};

// Semantic color tokens
export const semantic = {
  // Primary brand colors
  primary: {
    main: blue[500],
    light: blue[300],
    dark: blue[700],
    contrastText: '#ffffff',
  },
  
  // Secondary brand colors
  secondary: {
    main: grey[600],
    light: grey[400],
    dark: grey[800],
    contrastText: '#ffffff',
  },
  
  // Status colors
  success: {
    main: green[500],
    light: green[300],
    dark: green[700],
    contrastText: '#ffffff',
  },
  
  warning: {
    main: orange[500],
    light: orange[300],
    dark: orange[700],
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  
  error: {
    main: red[500],
    light: red[300],
    dark: red[700],
    contrastText: '#ffffff',
  },
  
  info: {
    main: blue[500],
    light: blue[300],
    dark: blue[700],
    contrastText: '#ffffff',
  },
  
  // Background colors
  background: {
    default: '#ffffff',
    paper: grey[50],
    elevated: '#ffffff',
    panel: grey[50],
  },
  
  // Text colors
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    hint: 'rgba(0, 0, 0, 0.38)',
  },
  
  // Action colors
  action: {
    active: blue[500],
    hover: 'rgba(0, 0, 0, 0.04)',
    hoverOpacity: 0.04,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(0, 0, 0, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },
  
  // Divider color
  divider: 'rgba(0, 0, 0, 0.12)',
};

// Panel-specific background colors
export const panelBackgrounds = {
  default: grey[50],        // Default panels
  help: purple[50],         // Help dialogs
  info: blue[50],          // Info panels
  warning: orange[50],      // Warning panels
  success: green[50],       // Success panels
  elevated: '#ffffff',      // Elevated panels
};

// Special UI colors
export const special = {
  // Selection colors
  selection: {
    background: 'rgba(33, 150, 243, 0.15)',
    border: 'rgba(33, 150, 243, 0.5)',
    hover: 'rgba(33, 150, 243, 0.18)',
  },
  
  // Focus colors
  focus: {
    outline: blue[500],
    shadow: `0 0 0 3px ${blue[200]}`,
  },
  
  // Overlay colors
  overlay: {
    light: 'rgba(255, 255, 255, 0.9)',
    dark: 'rgba(0, 0, 0, 0.5)',
  },
};

export default {
  blue,
  grey,
  green,
  orange,
  red,
  purple,
  semantic,
  panelBackgrounds,
  special,
};